import React from "react";
import i18next from "src/i18n/config";

import type { Edge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/types";
import type { ColumnDragProps } from "@shared/table/TableHeader";
import { showErrorToast } from "@shared/toasts/Toasts";
import { Table } from "@tanstack/react-table";

import type * as ColumnDragAdapter from "./columnDragAdapter";
import { DeviceTableData, pinnedColumnIds } from "./DeviceTable.utils";

let adapter: typeof ColumnDragAdapter | null = null;
let adapterRequest: Promise<void> | null = null;
const adapterListeners = new Set<() => void>();

const loadAdapter = () => {
    adapterRequest ??= import("./columnDragAdapter").then(
        (module) => {
            adapter = module;
            adapterListeners.forEach((listener) => listener());
        },
        () => {
            adapterRequest = null;
            showErrorToast(i18next.t("common:error.failedToLoadFeature"));
        },
    );
};

const subscribeToAdapter = (listener: () => void) => {
    adapterListeners.add(listener);
    return () => {
        adapterListeners.delete(listener);
    };
};

const useAdapter = () => React.useSyncExternalStore(subscribeToAdapter, () => adapter);

export const useColumnDrag = (table: Table<DeviceTableData>, columnId: string): ColumnDragProps | undefined => {
    const cellRef = React.useRef<HTMLTableCellElement | null>(null);
    const handleRef = React.useRef<HTMLSpanElement | null>(null);
    const [dropEdge, setDropEdge] = React.useState<Edge | null>(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const [isColumnDragActive, setIsColumnDragActive] = React.useState(false);
    const pinned = pinnedColumnIds.includes(columnId);
    const loadedAdapter = useAdapter();

    React.useEffect(() => {
        const cell = cellRef.current;
        const handle = handleRef.current;
        if (pinned || !loadedAdapter || !cell || !handle) {
            return;
        }

        return loadedAdapter.registerColumn({
            table,
            columnId,
            cell,
            handle,
            onDraggingChange: setIsDragging,
            onDropEdgeChange: setDropEdge,
            onColumnDragActiveChange: setIsColumnDragActive,
        });
    }, [loadedAdapter, table, columnId, pinned]);

    return pinned
        ? undefined
        : {
              cellRef,
              handleRef,
              onCellPointerEnter: loadAdapter,
              dropEdge,
              isDragging,
              isDropTarget: isColumnDragActive && !isDragging,
          };
};

export const useColumnReorder = (table: Table<DeviceTableData>, scrollContainer: HTMLElement | null) => {
    const loadedAdapter = useAdapter();

    React.useEffect(() => {
        if (!loadedAdapter || !scrollContainer) {
            return;
        }

        return loadedAdapter.registerColumnReorder(table, scrollContainer);
    }, [loadedAdapter, table, scrollContainer]);
};
